import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { isNull } from 'lodash-es'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { EntityListCard } from 'Components/EntityListCard/EntityListCard'
import type { EntityProps } from 'Components/EntityListCard/EntityListCard.types'

import {
  getFullContactName,
  isAnonymisedContact,
  maskAnonymisedContactValue,
} from 'Utilities'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import type { EmployeeEntityCardProps } from './EmployeeEntityCard.types'

export const EmployeeEntityCard = observer((props: EmployeeEntityCardProps) => {
  const intl = useIntl()
  const { employee } = props
  const { organisationId } = useParams<{
    organisationId?: string
  }>()

  const generateEntity = useMemo((): EntityProps => {
    // the detail page of an anonymised employee holds nothing to read any more, so the card
    // states who the record belongs to without offering the way there
    const isAnonymised = isAnonymisedContact(employee?.contact)

    const link = isAnonymised
      ? undefined
      : composePath({
          params: {
            employeeId: employee?.id || '0',
            organisationId,
          },
          path: GLOBAL_ROUTE_PATHS.organisation.employee,
        })

    return {
      content: (
        <>
          {employee?.contact.email && (
            <p className="mb-0 mt-2 fs-6 text-truncate">
              <FormattedMessage id="Email" />:{' '}
              <span>
                {maskAnonymisedContactValue(
                  employee?.contact.email,
                  employee?.contact,
                  intl,
                )}
              </span>
            </p>
          )}

          {employee?.contact.phoneNumber && (
            <p className="mb-0 mt-2 fs-6 text-truncate">
              <FormattedMessage id="Phone_number" />:{' '}
              <span>
                {maskAnonymisedContactValue(
                  employee?.contact.phoneNumber,
                  employee?.contact,
                  intl,
                )}
              </span>
            </p>
          )}
        </>
      ),
      id: employee?.id ?? '0',
      link,
      title: getFullContactName(employee?.contact, { intl }),
    }
  }, [employee?.contact, employee?.id, intl, organisationId])

  if (isNull(employee)) {
    return null
  }

  return <EntityListCard entities={[generateEntity]} title="Employee" />
})
